import { Users, Ruler, CalendarClock, Hammer } from 'lucide-react';

import Head from 'next/head';
import Header from '../components/Header';
import Hero from '../components/Hero';
import Split from '../components/Split';
import CompanyHistory from '../components/CompanyHistory';
import ProcessRoadmap from '../components/ProcessRoadmap';
import CTASection from '../components/CTASection';
import Services from '../components/Services';
import SocialProof from '../components/SocialProof';
import ContactUs from '../components/ContactUs';
import Footer from '../components/Footer';
import { generateWebPageSchema } from '../utils/generateWebPageSchema';

export default function About() {

  const base = 'https://modernbuildremodel.com';
  const pageUrl = `${base}/about-us`;

  // Breadcrumb JSON-LD
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": base },
      { "@type": "ListItem", "position": 2, "name": "About Us", "item": pageUrl }
    ]
  };


  return (
    <>
      <Head>
        <title>Learn More About Us | Modern Build</title>
        <meta name="title" content="Learn More About Us | Modern Build" />
        <meta name="description" content="Learn more about Modern Build — a trusted home remodeling company dedicated to quality, craftsmanship, and client satisfaction. Serving Greater Los Angeles with passion and precision." />
        <meta name="keywords" content="about Modern Build, home remodeling team, construction experts, los angeles builders, renovation company, client-focused remodeling"/>
        <meta name="author" content="Modern Build Home Remodeling" />

        {/* Canonical URL to avoid duplicate content issues */}
        <link rel="canonical" href="https://modernbuildremodel.com/about-us" />

        {/* Open Graph Meta Tags for social sharing */}
        <meta property="og:title" content="Learn More About Us | Modern Build" />
        <meta property="og:description" content="Meet the expert team behind Modern Build. Discover our story, values, and process — and why we're trusted by homeowners across Los Angeles for beautiful, high-quality remodeling work." />
        <meta property="og:image" content="https://modernbuildremodel.com/hero/home-remodling-contractor-experts-in-los-angeles.webp" />
        <meta property="og:url" content="https://modernbuildremodel.com/about-us" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />
        <meta property="og:type" content="website" />

        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Learn More About Us | Modern Build" />
        <meta name="twitter:description" content="Discover who we are and what drives us. Learn about Modern Build’s mission, values, and proven remodeling process that makes us a go-to contractor in the Greater LA area." />
        <meta name="twitter:image" content="https://modernbuildremodel.com/hero/home-remodling-contractor-experts-in-los-angeles.webp" />
        <meta name="twitter:site" content="@modernbuildhome" />
  

       {/* JSON-LD: WebPage */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(generateWebPageSchema({
              title: 'About Modern Build | Expert Home Remodelers',
              description: 'Learn more about Modern Build — our mission, values, and experience in remodeling homes across Southern California.',
              slug: '/about-us'
            }))
          }}
        />

        {/* JSON-LD: Breadcrumb */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              generateWebPageSchema({
                title: 'About Modern Build | Expert Home Remodelers',
                description: 'Learn more about Modern Build — our mission, values, and experience in remodeling homes across Southern California.',
                slug: '/about-us'
              })
            )
          }}
        />
        
      </Head>

      <Header />
      <Hero category="about" page="page" hasContentH1={false}/>
      <Split
        subtitle="Modern Build"
        title="Our Story Is Built on Craftsmanship"
        background="white"
        description="Modern Build started with a simple mission: to deliver luxury remodeling without compromise. Our founders brought together decades of experience in high-end renovations, with a passion for precision and beautiful design."
        extraText="We’re not just builders — we’re partners in your vision. From initial consultation to the final finish, every detail matters. Serving Greater Los Angeles with pride and professionalism."
        phone="(323) 300-4130"
        image1="/split/best-kitchen-remodeling-in-los-angeles-ca.webp"
        image2="/split/best-bathroom-remodeling-in-los-angeles-ca.webp"
        reverse={false}
      />
      <ProcessRoadmap
        title="Our Process"
        subtitle="Step-by-step from concept to completion"
        steps={[
          {
            icon: <Users className="w-10 h-10 text-primary" />,
            title: "Consultation",
            description:
              "Every great project starts with a conversation. We meet to understand your vision, needs, and lifestyle, ensuring your goals guide the process.",
          },
          {
            icon: <Ruler className="w-10 h-10 text-primary" />,
            title: "Design & Materials",
            description:
              "Our designers help you select layouts, materials, and finishes that match your aesthetic and budget — blending beauty with functionality.",
          },
          {
            icon: <CalendarClock className="w-10 h-10 text-primary" />,
            title: "Planning & Timeline",
            description:
              "We finalize detailed plans and establish a clear, efficient timeline. Permits, prep work, and communication all handled by our team.",
          },
          {
            icon: <Hammer className="w-10 h-10 text-primary" />,
            title: "Build & Follow-Up",
            description:
              "Our skilled craftsmen bring your design to life. We manage every detail and complete a thorough quality check before your final walkthrough.",
          },
        ]}
      />
      <Split
        subtitle="Modern Build"
        title="Our Mission"
        background="white"
        description="Our mission is to deliver honest, high-quality remodeling with a personal touch that exceeds expectations. We believe every home deserves to be a true reflection of the people who live in it."
        extraText="From your first consultation to the final walkthrough, we prioritize transparency, proactive communication, and unmatched craftsmanship. Our commitment is to treat your home with the same care and attention we’d give our own — ensuring you're proud of the results for years to come."
        phone="(323) 300-4130"
        image1="/split/kitchen-remodeling-in-los-angeles-ca.webp"
        image2="/split/home-remodeling-in-los-angeles-ca.webp"
        reverse={true}
      />
      <CTASection
        title="Let’s Build Something Beautiful"
        subtitle="Modern Build"
        buttonText="Get a Free Estimate"
        link="/contact"
        backgroundImage="/hero/best-home-remodling-contractor-in-los-angeles.webp"
      />
      <Split
        subtitle="Modern Build"
        title="Our Vision"
        background="white"
        description="We aim to become Los Angeles’s most trusted name in home remodeling — known not just for exceptional work, but for how we make people feel throughout the process."
        extraText="Our vision is to build more than homes; we build long-term relationships. Through quality, integrity, and innovation, we strive to create living spaces that improve lifestyles and stand the test of time — both structurally and emotionally."
        phone="(323) 300-4130"
        image1="/split/best-kitchen-remodeling-contractor-in-los-angeles-ca.webp"
        image2="/split/best-home-remodeling-in-los-angeles-ca.webp"
        reverse={false}
      />
      <Services category="home" />
      <CompanyHistory
        title="Our Story"
        subtitle="Modern Build didn’t just happen — it grew from the ground up."
        events={[
          {
            year: "2010",
            description:
              "Modern Build was founded by a group of skilled craftsmen in Los Angeles, driven by a shared passion for home improvement.",
          },
          {
            year: "2014",
            description:
              "We expanded into full-service remodeling, offering kitchens, bathrooms, roofing, and more across Southern California.",
          },
          {
            year: "2018",
            description:
              "Opened our in-house design studio, giving clients hands-on support with material selection and layout planning.",
          },
          {
            year: "2023",
            description:
              "Recognized as one of LA's top remodeling teams — with hundreds of happy clients and beautifully crafted spaces.",
          },
        ]}
      />
      <SocialProof />
      <ContactUs />
      <Footer />
    </>
  );
}
